import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import ApiURl from "../controllers/Api";
import imgLocation from "../controllers/imagePath";
import CheckoutProductsView from "./checkoutProductsview";
import CheckoutReceipt from "./CheckoutReceipt";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const emptyAddress = {
  full_name: "",
  phone: "",
  address_line1: "",
  address_line2: "",
  city: "",
  state: "",
  pincode: "",
};

function Checkout() {
  const navigate = useNavigate();

  const [loading, setLoading] = useState({
    addresses: true,
    cart: true,
    submitting: false,
  });

  const [addressForm, setAddressForm] = useState(emptyAddress);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [showAllAddresses, setShowAllAddresses] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [toast, setToast] = useState({ show: false, type: "info", msg: "" });
  const [saveAddress, setSaveAddress] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);

  const pageRef = useRef(null);
  const formRef = useRef(null);
  const summaryRef = useRef(null);

  const cartId = localStorage.getItem("cart_id");
  const userId = localStorage.getItem("user_id");

  const showToast = useCallback((msg, type = "info") => {
    setToast({ show: true, type, msg });
    setTimeout(() => {
      setToast({ show: false, type: "info", msg: "" });
    }, 2800);
  }, []);

  const mapAddressToForm = useCallback((addr) => {
    return {
      full_name: addr?.full_name || "",
      phone: String(addr?.phone || addr?.mobile_no || addr?.mobile || ""),
      address_line1: addr?.address_line1 || "",
      address_line2: addr?.address_line2 || "",
      city: addr?.city || "",
      state: addr?.state || "",
      pincode: String(addr?.pincode || addr?.landmark || ""),
    };
  }, []);

  // Load checkout data
  useEffect(() => {
    let mounted = true;

    const mergeItemData = (item, sources) => {
      const match = sources.find((source) => {
        const sameCartItem =
          item?.cart_item_id &&
          source?.cart_item_id &&
          String(item.cart_item_id) === String(source.cart_item_id);
        const sameProduct =
          item?.product_id &&
          source?.product_id &&
          String(item.product_id) === String(source.product_id);

        return sameCartItem || sameProduct;
      });

      if (!match) return item;

      return {
        ...match,
        ...item,
        name: item?.name || item?.product_name || match?.name || match?.product_name,
        product_name: item?.product_name || item?.name || match?.product_name || match?.name,
        img_path: item?.img_path || match?.img_path,
        image: item?.image || match?.image,
        image_url: item?.image_url || match?.image_url,
        imageUrl: item?.imageUrl || match?.imageUrl,
        image_path: item?.image_path || match?.image_path,
        product_image: item?.product_image || match?.product_image,
        thumbnail: item?.thumbnail || match?.thumbnail,
        images: item?.images || match?.images,
        image_base_url: item?.image_base_url || match?.image_base_url,
        metal: item?.metal || match?.metal,
        purity: item?.purity || match?.purity,
        is_purity_price: item?.is_purity_price ?? match?.is_purity_price,
      };
    };

    const enrichCheckoutData = async (parsed) => {
      const localSources = [];

      ["checkout_items", "cart"].forEach((key) => {
        try {
          const saved = JSON.parse(localStorage.getItem(key) || "[]");
          if (Array.isArray(saved)) localSources.push(...saved);
        } catch (error) {}
      });

      let items = (parsed?.items || []).map((item) => mergeItemData(item, localSources));

      const needsImages = items.some(
        (item) =>
          !item?.img_path &&
          !item?.image &&
          !item?.image_url &&
          !item?.image_path &&
          !item?.images
      );

      if (needsImages && cartId) {
        try {
          const response = await fetch(`${ApiURl}getCartItems.php?cart_id=${encodeURIComponent(cartId)}`);
          const cartResponse = await response.json();
          const serverItems = cartResponse?.data || cartResponse?.cart_items || cartResponse?.items || [];
          if (Array.isArray(serverItems) && serverItems.length) {
            items = items.map((item) => mergeItemData(item, serverItems));
          }
        } catch (error) {
          console.error("Unable to enrich checkout item images:", error);
        }
      }

      return { ...parsed, items };
    };

    try {
      const savedData = localStorage.getItem("checkout_data");
      if (savedData) {
        const parsed = JSON.parse(savedData);
        enrichCheckoutData(parsed).then((nextData) => {
          if (!mounted) return;
          setCheckoutData(nextData);
          localStorage.setItem("checkout_data", JSON.stringify(nextData));
          setLoading((prev) => ({ ...prev, cart: false }));
        });
      } else {
        showToast("No checkout data found", "error");
        navigate("/cart");
      }
    } catch (error) {
      console.error("Error loading checkout data:", error);
      navigate("/cart");
    }
    return () => {
      mounted = false;
    };
  }, [navigate, showToast, cartId]);

  // Fetch addresses
  useEffect(() => {
    const fetchAddresses = async () => {
      if (!userId) {
        showToast("Please log in to continue", "error");
        setLoading((prev) => ({ ...prev, addresses: false }));
        return;
      }

      try {
        const response = await fetch(`${ApiURl}/getUserAddress.php?user_id=${userId}`);
        const data = await response.json();

        const addresses = data?.data?.addresses || [];
        setSavedAddresses(addresses);

        const defaultAddr =
          addresses.find((a) => Number(a.is_default) === 1 || a.is_default === true) || addresses[0];

        if (defaultAddr) {
          setSelectedAddressId(defaultAddr.address_id);
          setAddressForm(mapAddressToForm(defaultAddr));
        } else {
          setSelectedAddressId(null);
          setAddressForm(emptyAddress);
        }
      } catch (error) {
        console.error("Error fetching addresses:", error);
        showToast("Failed to load addresses", "error");
      } finally {
        setLoading((prev) => ({ ...prev, addresses: false }));
      }
    };

    fetchAddresses();
  }, [userId, showToast, mapAddressToForm]);

  const addressesToDisplay = useMemo(() => {
    if (showAllAddresses) return savedAddresses;

    const defaultOnly = savedAddresses.filter(
      (a) => Number(a.is_default) === 1 || a.is_default === true
    );

    return defaultOnly.length ? defaultOnly : savedAddresses.slice(0, 1);
  }, [savedAddresses, showAllAddresses]);

  const handleAddressSelect = useCallback(
    (addr) => {
      setSelectedAddressId(addr.address_id);
      setAddressForm(mapAddressToForm(addr));
    },
    [mapAddressToForm]
  );

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;

    let updatedValue = value;

    if (name === "phone") {
      updatedValue = value.replace(/\D/g, "").slice(0, 10);
    }

    if (name === "pincode") {
      updatedValue = value.replace(/\D/g, "").slice(0, 6);
    }

    setAddressForm((prev) => ({
      ...prev,
      [name]: updatedValue,
    }));

    // Once user edits, treat it like a new/custom address
    setSelectedAddressId(null);
  }, []);

  const handleUseNewAddress = useCallback(() => {
    setSelectedAddressId(null);
    setAddressForm(emptyAddress);
  }, []);

  const handleModalClose = useCallback(() => {
    setIsModalVisible(false);
    localStorage.removeItem("checkout_data");
    navigate("/");
    window.scrollTo(0, 0);
  }, [navigate]);

  const getCouponInfo = useCallback(() => {
    const couponObj = checkoutData?.coupon || null;

    const coupon_id = couponObj?.id || checkoutData?.coupon_id || 0;
    const coupon_code = couponObj?.code || checkoutData?.coupon_code || "";
    const coupon_discount =
      Number(
        couponObj?.discount ??
          checkoutData?.couponDiscount ??
          checkoutData?.coupon_discount ??
          0
      ) || 0;

    return { coupon_id, coupon_code, coupon_discount };
  }, [checkoutData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (loading.submitting || !checkoutData) return;

    const requiredFields = [
      "full_name",
      "phone",
      "address_line1",
      "address_line2",
      "city",
      "state",
    ];

    const missingFields = requiredFields.filter(
      (field) => !String(addressForm[field] || "").trim()
    );

    if (missingFields.length > 0) {
      showToast("Please fill all required fields", "error");
      return;
    }

    if (!/^[0-9]{10}$/.test(addressForm.phone || "")) {
      showToast("Please enter a valid 10-digit mobile number", "error");
      return;
    }

    if (addressForm.pincode && !/^[0-9]{6}$/.test(addressForm.pincode)) {
      showToast("Please enter a valid 6-digit pincode", "error");
      return;
    }

    setLoading((prev) => ({ ...prev, submitting: true }));

    try {
      const subtotal = checkoutData.items.reduce(
        (sum, item) =>
          sum + Number(item.price || item.discount_price || item.original_price || 0) * Number(item.quantity || 1),
        0
      );

      const { coupon_id, coupon_code, coupon_discount } = getCouponInfo();
      const discountedSubtotal = Math.max(0, subtotal - coupon_discount);

      let gstTotal = 0;
      if (addressForm.state) {
        const isDelhi = addressForm.state.toLowerCase().trim() === "delhi";

        checkoutData.items.forEach((item) => {
          const itemOriginalTotal =
            Number(item.price || item.discount_price || item.original_price || 0) * Number(item.quantity || 1);
          const itemRatio = subtotal ? itemOriginalTotal / subtotal : 0;
          const itemDiscountedTotal = discountedSubtotal * itemRatio;

          if (isDelhi) {
            gstTotal +=
              (itemDiscountedTotal *
                (Number(item.sgst || 0) + Number(item.cgst || 0))) /
              100;
          } else {
            gstTotal += (itemDiscountedTotal * Number(item.igst || 0)) / 100;
          }
        });
      }

      const finalTotal = Number((discountedSubtotal + gstTotal).toFixed(2));

      const orderData = {
        user_id: userId,
        cart_id: cartId,
        address: {
          ...addressForm,
          user_id: userId,
          address_id: selectedAddressId || null,
        },
       items: checkoutData.items.map((item) => ({
  product_id: item.product_id,
  quantity: item.quantity,
  price: item.price || item.discount_price || item.original_price,
  original_price: item.original_price,
  total: Number(item.price || item.discount_price || item.original_price || 0) * Number(item.quantity || 1),
  size: item.size,
  sgst: item.sgst || 0,
  cgst: item.cgst || 0,
  igst: item.igst || 0,

  discount_type: item.discount_type || "",
  discount_value: item.discount_value || 0,

  selected: 1,
})),

        coupon_id: coupon_id || 0,
        coupon_code: coupon_code || "",
        coupon_discount: coupon_discount || 0,

        total: finalTotal,
        payment_method: "COD",
        gst_total: Number(gstTotal.toFixed(2)),
      };


      const orderResponse = await fetch(`${ApiURl}/createOrder.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(orderData),
      });

      const orderResult = await orderResponse.json();

      if (!orderResult.success || !orderResult.order_id) {
        throw new Error(orderResult.message || "Failed to create order");
      }

      const invoiceUrl = `${ApiURl}/download_invoice.php?invoice_number=${orderResult.order_id}`;
      const pdfResponse = await fetch(invoiceUrl);

      if (!pdfResponse.ok) {
        throw new Error(`Failed to fetch invoice: HTTP ${pdfResponse.status}`);
      }

      const contentType = pdfResponse.headers.get("Content-Type") || "";
      if (!contentType.includes("application/pdf")) {
        throw new Error(`Invalid PDF response: ${contentType}`);
      }

      const pdfBlob = await pdfResponse.blob();
      const filename =
        pdfResponse.headers.get("X-Invoice-Filename") ||
        `invoice_${orderResult.order_id}.pdf`;

      const formData = new FormData();
      formData.append("file", pdfBlob, filename);

      // const uploadResponse = await $.ajax({
      //   url: `${ApiURl}/proxyUpload.php`,
      //   type: "POST",
      //   data: formData,
      //   processData: false,
      //   contentType: false,
      //   dataType: "json",
      // });

      // if (uploadResponse?.status !== "success" || !uploadResponse?.public_url) {
      //   throw new Error(uploadResponse?.message || "PDF upload failed");
      // }

      // // WhatsApp data
      // let normalizedPhone = String(addressForm.phone || "").replace(/\D/g, "");
      // if (normalizedPhone.length === 10) normalizedPhone = "91" + normalizedPhone;
      // if (normalizedPhone.length === 13 && normalizedPhone.startsWith("91"))
      //   normalizedPhone = normalizedPhone.slice(0, 12);

      // await $.ajax({
      //   url: `${ApiURl}/proxySendWhatsApp.php`,
      //   type: "POST",
      //   dataType: "json",
      //   contentType: "application/json; charset=utf-8",
      //   data: JSON.stringify({
      //     user_name: addressForm.full_name || "Customer",
      //     user_phone: normalizedPhone,
      //     public_url: uploadResponse.public_url,
      //   }),
      // });

      await fetch(`${ApiURl}/clearCart.php`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ cart_id: cartId }),
      });

      localStorage.removeItem("cart");
      localStorage.removeItem("totalAmount");
      localStorage.removeItem("checkout_data");
      localStorage.removeItem("applied_coupon");

      setIsModalVisible(true);
      showToast("Order placed successfully!", "success");

      window.dispatchEvent(new Event("storage"));
    } catch (error) {
      console.error("Order submission error:", error);
      showToast(error.message || "Failed to place order", "error");
    } finally {
      setLoading((prev) => ({ ...prev, submitting: false }));
    }
  };

  const accent = "#b29063";
  const inputBase =
    "mt-1 w-full rounded-xl border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 outline-none focus:ring-2 transition";

  const uiTotals = useMemo(() => {
    if (!checkoutData) {
      return { subtotal: 0, coupon_discount: 0, gst: 0, final: 0 };
    }

    const subtotal = checkoutData.items.reduce(
      (sum, item) => sum + Number(item.price || 0) * Number(item.quantity || 1),
      0
    );

    const coupon_discount =
      Number(
        checkoutData?.coupon?.discount ??
          checkoutData?.couponDiscount ??
          checkoutData?.coupon_discount ??
          0
      ) || 0;

    const discountedSubtotal = Math.max(0, subtotal - coupon_discount);

    let gst = 0;
    if (addressForm.state) {
      const isDelhi = addressForm.state.toLowerCase().trim() === "delhi";

      checkoutData.items.forEach((item) => {
        const itemOriginalTotal = Number(item.price || 0) * Number(item.quantity || 1);
        const itemRatio = subtotal ? itemOriginalTotal / subtotal : 0;
        const itemDiscountedTotal = discountedSubtotal * itemRatio;

        if (isDelhi) {
          gst +=
            (itemDiscountedTotal *
              (Number(item.sgst || 0) + Number(item.cgst || 0))) /
            100;
        } else {
          gst += (itemDiscountedTotal * Number(item.igst || 0)) / 100;
        }
      });
    }

    const final = Number((discountedSubtotal + gst).toFixed(2));

    return {
      subtotal,
      coupon_discount,
      gst: Number(gst.toFixed(2)),
      final,
    };
  }, [checkoutData, addressForm.state]);

  const formatINR = (value) =>
    `₹${Number(value || 0).toLocaleString("en-IN", {
      maximumFractionDigits: 2,
    })}`;

  const FALLBACK_IMG =
    "data:image/svg+xml;utf8," +
    encodeURIComponent(`
      <svg xmlns="http://www.w3.org/2000/svg" width="300" height="300">
        <rect width="100%" height="100%" fill="#f5eee5"/>
        <text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle"
          font-family="Arial" font-size="16" fill="#8d7b73">No Image</text>
      </svg>
    `);

  const normalizeImage = useCallback((item) => {
    const pickImageValue = (value) => {
      if (!value) return "";
      if (typeof value === "string") return value;
      return value?.url || value?.filename || value?.image_url || value?.image_path || value?.img_path || "";
    };

    const joinImageUrl = (path) => {
      if (path == null) return "";
      let cleaned = String(path).trim().replace(/^['"]|['"]$/g, "");
      if (!cleaned) return "";
      if (/^(https?:|data:)/i.test(cleaned)) return cleaned;

      cleaned = cleaned.replace(/^\.\//, "").replace(/^\/+/, "");

      const imageBase = String(
        item?.image_base_url || localStorage.getItem("image_base_url") || imgLocation
      ).replace(/\/+$/, "");

      const baseEndsWithImages = /\/images$/i.test(imageBase);
      if (baseEndsWithImages && /^images\//i.test(cleaned)) {
        cleaned = cleaned.replace(/^images\//i, "");
      }

      return `${imageBase}/${cleaned}`.replace(/([^:]\/)\/+/g, "$1");
    };

    const candidates = [];

    if (Array.isArray(item?.images) && item.images.length) {
      const first = item.images[0];
      candidates.push(first?.url || first?.filename || first?.image_url || first?.image_path || first);
    }

    candidates.push(
      item?.image,
      item?.image_url,
      item?.imageUrl,
      item?.img_path,
      item?.image_path,
      item?.product_image,
      item?.thumbnail
    );

    for (const raw of candidates) {
      if (!raw) continue;

      if (typeof raw === "string") {
        const value = raw.trim();
        if (!value) continue;

        try {
          const parsed = JSON.parse(value);
          if (Array.isArray(parsed) && parsed.length) {
            const url = joinImageUrl(pickImageValue(parsed[0]));
            if (url) return url;
          }
          if (parsed && typeof parsed === "object") {
            const url = joinImageUrl(pickImageValue(parsed));
            if (url) return url;
          }
        } catch (error) {}

        const firstPart = value
          .split(",")
          .map((part) => part.trim())
          .filter(Boolean)[0];

        const url = joinImageUrl(firstPart || value);
        if (url) return url;
      } else {
        const url = joinImageUrl(pickImageValue(raw));
        if (url) return url;
      }
    }

    return FALLBACK_IMG;
  }, []);

  useEffect(() => {
    if (loading.addresses || loading.cart || !pageRef.current) return;

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    const context = gsap.context(() => {
      gsap.fromTo(
        ".checkout-step",
        { opacity: 0, y: -18 },
        { opacity: 1, y: 0, duration: 0.75, stagger: 0.12, ease: "power3.out" }
      );

      gsap.fromTo(
        formRef.current,
        { opacity: 0, x: -38 },
        { opacity: 1, x: 0, duration: 0.9, ease: "power3.out" }
      );

      gsap.fromTo(
        summaryRef.current,
        { opacity: 0, x: 38 },
        { opacity: 1, x: 0, duration: 0.9, ease: "power3.out", delay: 0.08 }
      );

      gsap.utils.toArray(".checkout-reveal").forEach((element) => {
        gsap.fromTo(
          element,
          { opacity: 0, y: 28 },
          {
            opacity: 1,
            y: 0,
            duration: 0.75,
            ease: "power3.out",
            scrollTrigger: {
              trigger: element,
              start: "top 90%",
              toggleActions: "play none none reverse",
            },
          }
        );
      });
    }, pageRef);

    const timer = window.setTimeout(() => ScrollTrigger.refresh(), 300);
    return () => {
      window.clearTimeout(timer);
      context.revert();
    };
  }, [loading.addresses, loading.cart, checkoutData]);

  if (loading.addresses && loading.cart) {
    return (
      <div className="checkout-loading">
        <div className="checkout-loader" />
        <p>Preparing your secure checkout...</p>
      </div>
    );
  }

  const items = checkoutData?.items || [];

  return (
    <section ref={pageRef} className="checkout-page">
      <form onSubmit={handleSubmit} className="checkout-shell">
        <div className="checkout-steps" aria-label="Checkout progress">
          {[
            ["1", "Shipping", "Address"],
            ["2", "Payment", "Method"],
            ["3", "Review &", "Place Order"],
          ].map(([number, lineOne, lineTwo], index) => (
            <React.Fragment key={number}>
              <div className={`checkout-step ${index === 0 ? "is-active" : ""}`}>
                <span>{number}</span>
                <div>
                  <strong>{lineOne}</strong>
                  <small>{lineTwo}</small>
                </div>
              </div>
              {index < 2 && <div className="checkout-step-line" />}
            </React.Fragment>
          ))}
        </div>

        <div className="checkout-layout">
          <div ref={formRef} className="checkout-form-column">
            <section className="checkout-section checkout-reveal">
              <div className="checkout-section-heading">
                <div>
                  <h1>Shipping Address</h1>
                  <p>Enter your delivery address details</p>
                </div>

                {savedAddresses.length > 0 && (
                  <button type="button" onClick={handleUseNewAddress} className="text-button">
                    Use new address
                  </button>
                )}
              </div>

              {savedAddresses.length > 0 && (
                <div className="saved-address-strip">
                  <div className="saved-address-top">
                    <strong>Saved Addresses</strong>
                    <button type="button" onClick={() => setShowAllAddresses((value) => !value)}>
                      {showAllAddresses ? "Show default" : "View all"}
                    </button>
                  </div>

                  <div className="saved-address-list">
                    {addressesToDisplay.map((address) => (
                      <button
                        type="button"
                        key={address.address_id}
                        onClick={() => handleAddressSelect(address)}
                        className={`saved-address ${
                          selectedAddressId === address.address_id ? "selected" : ""
                        }`}
                      >
                        <span className="saved-radio" />
                        <span>
                          <strong>{address.full_name}</strong>
                          <small>
                            {address.address_line1}, {address.address_line2}, {address.city}
                          </small>
                        </span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="checkout-field-grid">
                <label>
                  <span>Full Name <b>*</b></span>
                  <input
                    type="text"
                    name="full_name"
                    value={addressForm.full_name || ""}
                    onChange={handleInputChange}
                    placeholder="Enter your full name"
                    required
                  />
                </label>

                <label>
                  <span>Phone Number <b>*</b></span>
                  <input
                    type="text"
                    name="phone"
                    value={addressForm.phone || ""}
                    onChange={handleInputChange}
                    placeholder="Enter 10-digit mobile number"
                    inputMode="numeric"
                    maxLength={10}
                    required
                  />
                </label>

                <label className="pincode-field">
                  <span>Pincode <b>*</b></span>
                  <div className="pincode-row">
                    <input
                      type="text"
                      name="pincode"
                      value={addressForm.pincode || ""}
                      onChange={handleInputChange}
                      placeholder="Enter pincode"
                      inputMode="numeric"
                      maxLength={6}
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        showToast(
                          /^[0-9]{6}$/.test(addressForm.pincode || "")
                            ? "Pincode looks valid"
                            : "Enter a valid 6-digit pincode",
                          /^[0-9]{6}$/.test(addressForm.pincode || "") ? "success" : "error"
                        )
                      }
                    >
                      Check Pincode
                    </button>
                  </div>
                </label>

                <label className="address-field">
                  <span>Address <b>*</b></span>
                  <textarea
                    name="address_line1"
                    value={addressForm.address_line1 || ""}
                    onChange={handleInputChange}
                    placeholder="House No., Building Name, Street Name, Area"
                    maxLength={150}
                    required
                  />
                  <small>{String(addressForm.address_line1 || "").length}/150</small>
                </label>

                <label>
                  <span>City <b>*</b></span>
                  <input
                    type="text"
                    name="city"
                    value={addressForm.city || ""}
                    onChange={handleInputChange}
                    placeholder="Enter city"
                    required
                  />
                </label>

                <label>
                  <span>State <b>*</b></span>
                  <select
                    name="state"
                    value={addressForm.state || ""}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">Select state</option>
                    {[
                      "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar",
                      "Chhattisgarh", "Delhi", "Goa", "Gujarat", "Haryana",
                      "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala",
                      "Madhya Pradesh", "Maharashtra", "Manipur", "Meghalaya",
                      "Mizoram", "Nagaland", "Odisha", "Punjab", "Rajasthan",
                      "Sikkim", "Tamil Nadu", "Telangana", "Tripura",
                      "Uttar Pradesh", "Uttarakhand", "West Bengal"
                    ].map((state) => <option key={state} value={state}>{state}</option>)}
                  </select>
                </label>

                <label>
                  <span>Landmark (Optional)</span>
                  <input
                    type="text"
                    name="address_line2"
                    value={addressForm.address_line2 || ""}
                    onChange={handleInputChange}
                    placeholder="Nearby landmark"
                    required
                  />
                </label>
              </div>

              <label className="save-address-check">
                <input
                  type="checkbox"
                  checked={saveAddress}
                  onChange={(event) => setSaveAddress(event.target.checked)}
                />
                <span>Save this address for future use</span>
              </label>
            </section>

            <section className="checkout-section payment-section checkout-reveal">
              <div className="checkout-section-heading">
                <div>
                  <h2>Payment Method</h2>
                  <p>Choose your preferred payment method</p>
                </div>
              </div>

              <button
                type="button"
                className="mobile-payment-trigger"
                onClick={() => setPaymentOpen((value) => !value)}
              >
                <span>Payment Method</span>
                <strong>Cash on Delivery</strong>
                <span>{paymentOpen ? "−" : "+"}</span>
              </button>

              <div className={`payment-desktop ${paymentOpen ? "mobile-open" : ""}`}>
                <div className="payment-options">
                  <label className="payment-option active">
                    <input type="radio" name="payment" checked readOnly />
                    <span>
                      <strong>Cash on Delivery</strong>
                      <small>Pay safely when your order arrives</small>
                    </span>
                    <b>COD</b>
                  </label>

                  <div className="payment-option disabled">
                    <span className="fake-radio" />
                    <span>
                      <strong>UPI</strong>
                      <small>Online UPI payment coming soon</small>
                    </span>
                    <b>UPI</b>
                  </div>

                  <div className="payment-option disabled">
                    <span className="fake-radio" />
                    <span>
                      <strong>Credit / Debit Card</strong>
                      <small>Visa, Mastercard, RuPay & more</small>
                    </span>
                    <b>VISA</b>
                  </div>

                  <div className="payment-option disabled">
                    <span className="fake-radio" />
                    <span>
                      <strong>Net Banking</strong>
                      <small>All major banks supported</small>
                    </span>
                    <b>Bank</b>
                  </div>
                </div>

                <div className="payment-detail-card">
                  <div className="cod-icon">₹</div>
                  <h3>Cash on Delivery</h3>
                  <p>Your existing checkout remains connected to the current COD order flow.</p>
                  <div className="payment-security">Secure and verified checkout</div>
                </div>
              </div>
            </section>

            <div className="desktop-security-note checkout-reveal">
              <span>▣</span>
              Your payment and personal details are secure and encrypted
            </div>
          </div>

          <aside ref={summaryRef} className="checkout-summary-column">
            <div className="order-summary-card">
              <div className="summary-heading">
                <h2>Order Summary</h2>
                <span>{items.length} Items</span>
              </div>

              <div className="summary-products">
                {items.map((item, index) => (
                  <article className="summary-product" key={`${item.product_id}-${index}`}>
                    <img
                      src={normalizeImage(item)}
                      alt={item.name || item.product_name || "Product"}
                      onError={(event) => {
                        event.currentTarget.onerror = null;
                        event.currentTarget.src = FALLBACK_IMG;
                      }}
                    />
                    <div>
                      <h3>{item.name || item.product_name || "Jewellery Product"}</h3>
                      <p>{item.metal || item.purity || "Gold Jewellery"}</p>
                      <p>
                        {Number(item.is_purity_price) === 1 ? "Purity" : "Size"}:{" "}
                        {item.size || item.purity || "N/A"}
                      </p>
                      <p>Qty: {item.quantity || 1}</p>
                    </div>
                    <strong>{formatINR(Number(item.price || 0) * Number(item.quantity || 1))}</strong>
                  </article>
                ))}
              </div>

              <button
                type="button"
                className="mobile-summary-toggle"
                onClick={() => setSummaryOpen((value) => !value)}
              >
                <span>View Details</span>
                <span>{summaryOpen ? "⌃" : "⌄"}</span>
              </button>

              <div className={`summary-totals ${summaryOpen ? "mobile-open" : ""}`}>
                <div><span>Subtotal ({items.length} Items)</span><strong>{formatINR(uiTotals.subtotal)}</strong></div>
                <div><span>Shipping</span><strong className="free">FREE</strong></div>
                {uiTotals.coupon_discount > 0 && (
                  <div><span>Discount</span><strong className="discount">-{formatINR(uiTotals.coupon_discount)}</strong></div>
                )}
                {uiTotals.gst > 0 && (
                  <div><span>GST</span><strong>{formatINR(uiTotals.gst)}</strong></div>
                )}
              </div>

              <div className="summary-grand-total">
                <div>
                  <span>Total Amount</span>
                  <small>(Inclusive of all taxes)</small>
                </div>
                <strong>{formatINR(uiTotals.final)}</strong>
              </div>
            </div>

            <div className="place-order-card checkout-reveal">
              <div className="trust-points">
                <div><span>◇</span><p><strong>Secure Payments</strong><small>100% safe & secure</small></p></div>
                <div><span>↻</span><p><strong>Easy Returns</strong><small>7 days return policy</small></p></div>
                <div><span>♢</span><p><strong>Hallmarked Jewellery</strong><small>Certified & trusted</small></p></div>
              </div>

              <button type="submit" className="place-order-button" disabled={loading.submitting}>
                <span>▣</span>
                {loading.submitting ? "Processing..." : "Place Order"}
              </button>

              <p>By placing this order, you agree to our <span>Terms & Conditions</span></p>
            </div>
          </aside>
        </div>
      </form>

      {toast.show && (
        <div className={`checkout-toast ${toast.type}`}>{toast.msg}</div>
      )}

      {isModalVisible && (
        <div className="checkout-modal-backdrop">
          <div className="checkout-success-modal">
            <div>✓</div>
            <h3>Order Confirmed!</h3>
            <p>Your order has been placed successfully. You will receive a confirmation on your mobile.</p>
            <button onClick={handleModalClose}>Continue Shopping</button>
          </div>
        </div>
      )}

      <style>{`
        .checkout-page, .checkout-page * { box-sizing: border-box; }
        .checkout-page {
          --maroon: var(--user-maroon, #2b0000);
          --deep-maroon: var(--user-maroon-deep, #190000);
          --gold: var(--user-gold, #d49a34);
          --line: var(--user-border, #dfcbbb);
          --cream: var(--user-card, #fffaf4);
          --muted: var(--user-text-soft, #6c5b55);
          min-height: 100vh;
          background: var(--user-bg, #E8D9D5);
          color: var(--user-text, #2a1715);
          font-family: "Kanit", Arial, sans-serif;
        }
        .checkout-shell { width: min(1440px, calc(100% - 36px)); margin: 0 auto; padding: 34px 0 52px; }
        .checkout-steps { display: grid; grid-template-columns: auto 1fr auto 1fr auto; align-items: center; max-width: 820px; margin: 0 0 38px; }
        .checkout-step { display: flex; align-items: center; gap: 14px; }
        .checkout-step > span { display: grid; place-items: center; width: 36px; height: 36px; border: 1px solid var(--gold); border-radius: 50%; color: #633717; background: #fff8ef; font-family: Georgia, serif; font-size: 18px; }
        .checkout-step.is-active > span { color: white; border-color: var(--maroon); background: var(--maroon); }
        .checkout-step strong, .checkout-step small { display: block; font-size: 13px; line-height: 1.25; }
        .checkout-step small { margin-top: 3px; color: var(--muted); }
        .checkout-step-line { height: 1px; margin: 0 24px; background: var(--line); }
        .checkout-layout { display: grid; grid-template-columns: minmax(0, 1fr) 440px; gap: 50px; align-items: start; }
        .checkout-form-column { min-width: 0; }
        .checkout-section { padding: 0 0 28px; border-bottom: 1px solid rgba(154, 102, 72, .22); }
        .checkout-section + .checkout-section { padding-top: 26px; }
        .checkout-section-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; margin-bottom: 22px; }
        .checkout-section-heading h1, .checkout-section-heading h2 { margin: 0; font-family: Georgia, serif; font-size: 25px; font-weight: 500; color: var(--user-text, #2c1715); }
        .checkout-section-heading p { margin: 5px 0 0; color: var(--muted); font-size: 13px; }
        .text-button { border: 0; background: transparent; color: var(--gold); font-weight: 600; cursor: pointer; }
        .saved-address-strip { margin-bottom: 22px; padding: 14px; border: 1px solid var(--line); border-radius: 9px; background: rgba(255,250,244,.5); }
        .saved-address-top { display: flex; justify-content: space-between; margin-bottom: 10px; font-size: 13px; }
        .saved-address-top button { border: 0; background: none; color: var(--gold); cursor: pointer; }
        .saved-address-list { display: grid; grid-template-columns: repeat(2, minmax(0,1fr)); gap: 10px; }
        .saved-address { display: flex; gap: 10px; padding: 11px; text-align: left; border: 1px solid var(--line); border-radius: 7px; background: white; cursor: pointer; }
        .saved-address.selected { border-color: var(--gold); background: #fff7e9; }
        .saved-address small { display: block; margin-top: 4px; color: var(--muted); line-height: 1.35; }
        .saved-radio { width: 14px; height: 14px; margin-top: 2px; border: 1px solid var(--gold); border-radius: 50%; box-shadow: inset 0 0 0 3px white; background: var(--gold); }
        .checkout-field-grid { display: grid; grid-template-columns: repeat(6, minmax(0,1fr)); gap: 20px 24px; }
        .checkout-field-grid > label { grid-column: span 3; }
        .checkout-field-grid > .pincode-field, .checkout-field-grid > .address-field { grid-column: 1 / -1; }
        .checkout-field-grid label > span { display: block; margin-bottom: 8px; font-size: 13px; font-weight: 600; }
        .checkout-field-grid label b { color: #b11e18; }
        .checkout-field-grid input, .checkout-field-grid select, .checkout-field-grid textarea { width: 100%; border: 1px solid var(--line); border-radius: 6px; background: rgba(255,255,255,.64); color: var(--user-text, #2a1715); outline: none; font: inherit; font-size: 14px; transition: border .2s, box-shadow .2s, background .2s; }
        .checkout-field-grid input, .checkout-field-grid select { height: 48px; padding: 0 14px; }
        .checkout-field-grid textarea { min-height: 104px; padding: 14px; resize: vertical; }
        .checkout-field-grid input:focus, .checkout-field-grid select:focus, .checkout-field-grid textarea:focus { border-color: var(--gold); background: white; box-shadow: 0 0 0 3px rgba(212,154,52,.11); }
        .address-field { position: relative; }
        .address-field > small { position: absolute; right: 12px; bottom: 10px; color: #88756d; font-size: 11px; }
        .pincode-row { display: grid; grid-template-columns: 1fr 240px; gap: 18px; }
        .pincode-row button { border: 1px solid var(--gold); border-radius: 6px; background: transparent; color: #a25c0d; font-weight: 600; cursor: pointer; }
        .save-address-check { display: flex; align-items: center; gap: 10px; margin-top: 19px; font-size: 13px; cursor: pointer; }
        .save-address-check input { width: 17px; height: 17px; accent-color: var(--maroon); }
        .payment-desktop { display: grid; grid-template-columns: 320px 1fr; gap: 22px; }
        .payment-options { display: grid; gap: 10px; }
        .payment-option { min-height: 62px; display: grid; grid-template-columns: auto 1fr auto; gap: 12px; align-items: center; padding: 10px 15px; border: 1px solid var(--line); border-radius: 6px; background: rgba(255,255,255,.55); }
        .payment-option.active { border-color: var(--gold); background: #fff8ec; }
        .payment-option.disabled { opacity: .55; }
        .payment-option input, .fake-radio { width: 18px; height: 18px; border: 1px solid var(--gold); border-radius: 50%; accent-color: var(--gold); }
        .payment-option strong, .payment-option small { display: block; }
        .payment-option strong { font-size: 14px; }
        .payment-option small { margin-top: 3px; color: var(--muted); font-size: 11px; }
        .payment-option > b { color: #7a501f; font-size: 12px; }
        .payment-detail-card { min-height: 290px; padding: 30px; border: 1px solid var(--line); border-radius: 9px; background: rgba(255,255,255,.5); text-align: center; }
        .cod-icon { display: grid; place-items: center; width: 82px; height: 82px; margin: 18px auto; border: 1px solid var(--gold); border-radius: 50%; color: var(--gold); font-family: Georgia, serif; font-size: 38px; }
        .payment-detail-card h3 { margin: 10px 0; font-family: Georgia, serif; font-size: 23px; }
        .payment-detail-card p { max-width: 300px; margin: 0 auto; color: var(--muted); line-height: 1.55; }
        .payment-security { margin: 24px auto 0; padding: 10px; border-top: 1px solid var(--line); color: #81531c; font-size: 12px; }
        .mobile-payment-trigger { display: none; }
        .desktop-security-note { display: flex; justify-content: center; gap: 10px; padding: 26px; color: #75615a; font-size: 12px; }
        .checkout-summary-column { position: sticky; top: 22px; display: grid; gap: 20px; }
        .order-summary-card, .place-order-card { border: 1px solid var(--line); border-radius: 9px; background: rgba(255,250,244,.62); box-shadow: 0 18px 45px rgba(64,20,12,.06); }
        .order-summary-card { padding: 26px 24px; }
        .summary-heading { display: flex; justify-content: space-between; align-items: center; margin-bottom: 18px; }
        .summary-heading h2 { margin: 0; font-family: Georgia, serif; font-size: 24px; font-weight: 500; }
        .summary-heading span { color: var(--muted); font-size: 12px; }
        .summary-products { display: grid; gap: 18px; padding-bottom: 20px; border-bottom: 1px solid var(--line); }
        .summary-product { display: grid; grid-template-columns: 70px 1fr auto; gap: 14px; align-items: center; }
        .summary-product img { width: 70px; height: 70px; object-fit: cover; border-radius: 7px; background: #f4e8dc; }
        .summary-product h3 { margin: 0 0 4px; font-family: Georgia, serif; font-size: 16px; font-weight: 500; }
        .summary-product p { margin: 2px 0; color: var(--muted); font-size: 11px; }
        .summary-product > strong { white-space: nowrap; font-size: 14px; }
        .summary-totals { display: grid; gap: 15px; padding: 20px 0; border-bottom: 1px solid var(--line); }
        .summary-totals > div { display: flex; justify-content: space-between; gap: 18px; font-size: 13px; }
        .summary-totals .free { color: #168b28; }
        .summary-totals .discount { color: #d01b16; }
        .summary-grand-total { display: flex; justify-content: space-between; align-items: flex-start; gap: 20px; padding-top: 20px; }
        .summary-grand-total span, .summary-grand-total small { display: block; }
        .summary-grand-total span { font-family: Georgia, serif; font-size: 18px; }
        .summary-grand-total small { margin-top: 7px; color: var(--muted); font-size: 11px; }
        .summary-grand-total > strong { font-family: Georgia, serif; font-size: 28px; }
        .mobile-summary-toggle { display: none; }
        .place-order-card { padding: 22px; }
        .trust-points { display: grid; gap: 17px; margin-bottom: 22px; }
        .trust-points > div { display: flex; align-items: center; gap: 14px; }
        .trust-points > div > span { color: var(--gold); font-size: 27px; }
        .trust-points p, .trust-points strong, .trust-points small { display: block; margin: 0; }
        .trust-points strong { font-size: 13px; }
        .trust-points small { margin-top: 4px; color: var(--muted); font-size: 11px; }
        .place-order-button { width: 100%; min-height: 56px; border: 0; border-radius: 5px; background: linear-gradient(100deg, var(--maroon), var(--deep-maroon), var(--maroon)); color: var(--user-gold-soft, #f3d696); font: 600 17px inherit; cursor: pointer; box-shadow: 0 12px 26px rgba(43,0,0,.16); }
        .place-order-button:disabled { opacity: .55; cursor: not-allowed; }
        .place-order-card > p { margin: 16px 0 0; text-align: center; color: var(--muted); font-size: 11px; }
        .place-order-card > p span { color: #a05d0d; }
        .checkout-toast { position: fixed; z-index: 100; left: 50%; bottom: 25px; transform: translateX(-50%); min-width: 240px; padding: 13px 20px; border-radius: 8px; color: white; text-align: center; background: #302825; box-shadow: 0 15px 40px rgba(0,0,0,.25); }
        .checkout-toast.success { background: #21713d; } .checkout-toast.error { background: #a92720; }
        .checkout-modal-backdrop { position: fixed; z-index: 100; inset: 0; display: grid; place-items: center; padding: 20px; background: rgba(20,0,0,.6); backdrop-filter: blur(4px); }
        .checkout-success-modal { width: min(430px, 100%); padding: 34px; border-radius: 18px; background: white; text-align: center; }
        .checkout-success-modal > div { display: grid; place-items: center; width: 70px; height: 70px; margin: 0 auto 18px; border-radius: 50%; color: white; background: #2f8b4b; font-size: 34px; }
        .checkout-success-modal h3 { margin: 0; font-family: Georgia, serif; font-size: 28px; }
        .checkout-success-modal p { color: var(--muted); line-height: 1.55; }
        .checkout-success-modal button { width: 100%; min-height: 48px; border: 0; border-radius: 6px; color: white; background: var(--maroon); font-weight: 600; cursor: pointer; }
        .checkout-loading { min-height: 100vh; display: grid; place-content: center; gap: 16px; text-align: center; background: var(--user-bg, #E8D9D5); }
        .checkout-loader { width: 48px; height: 48px; margin: auto; border: 4px solid rgba(43,0,0,.15); border-top-color: var(--maroon, #2b0000); border-radius: 50%; animation: checkout-spin .8s linear infinite; }
        @keyframes checkout-spin { to { transform: rotate(360deg); } }

        @media (max-width: 1000px) {
          .checkout-layout { grid-template-columns: 1fr 390px; gap: 24px; }
          .checkout-field-grid { gap: 16px; }
          .payment-desktop { grid-template-columns: 1fr; }
        }

        @media (max-width: 768px) {
          .checkout-shell { width: 100%; padding: 20px 14px 34px; }
          .checkout-steps { grid-template-columns: auto 1fr auto 1fr auto; margin: 0 4px 28px; }
          .checkout-step { display: block; text-align: center; }
          .checkout-step > span { width: 31px; height: 31px; margin: 0 auto 8px; font-size: 15px; }
          .checkout-step strong, .checkout-step small { font-size: 10px; }
          .checkout-step-line { margin: -27px 10px 0; }
          .checkout-layout { display: flex; flex-direction: column; gap: 20px; }
          .checkout-summary-column { position: static; order: 2; width: 100%; }
          .checkout-form-column { order: 1; width: 100%; }
          .checkout-section { padding-bottom: 22px; }
          .checkout-section-heading h1, .checkout-section-heading h2 { font-family: Arial, sans-serif; font-size: 18px; font-weight: 700; }
          .checkout-section-heading p { font-size: 12px; }
          .text-button, .saved-address-strip { display: none; }
          .checkout-field-grid { display: grid; grid-template-columns: repeat(2,minmax(0,1fr)); gap: 15px 14px; }
          .checkout-field-grid > label { grid-column: 1 / -1; }
          .checkout-field-grid > label:nth-of-type(5), .checkout-field-grid > label:nth-of-type(6) { grid-column: span 1; }
          .checkout-field-grid input, .checkout-field-grid select { height: 44px; font-size: 13px; }
          .checkout-field-grid textarea { min-height: 100px; font-size: 13px; }
          .pincode-row { grid-template-columns: 1fr 122px; gap: 12px; }
          .pincode-row button { font-size: 11px; }
          .save-address-check { margin-top: 15px; }
          .payment-section { padding-top: 4px !important; border-bottom: 0; }
          .payment-section > .checkout-section-heading { display: none; }
          .mobile-payment-trigger { width: 100%; display: grid; grid-template-columns: 1fr auto auto; gap: 12px; align-items: center; min-height: 52px; padding: 0 14px; border: 1px solid var(--line); border-radius: 7px; background: rgba(255,255,255,.58); text-align: left; }
          .mobile-payment-trigger strong { font-size: 12px; color: #80511d; }
          .payment-desktop { display: none; margin-top: 10px; }
          .payment-desktop.mobile-open { display: grid; }
          .payment-detail-card { display: none; }
          .desktop-security-note { display: none; }
          .order-summary-card { padding: 18px 14px; }
          .summary-heading { display: none; }
          .summary-products { display: none; }
          .summary-totals { display: none; border: 0; padding: 12px 0 0; }
          .summary-totals.mobile-open { display: grid; }
          .summary-grand-total { padding-top: 0; align-items: center; }
          .summary-grand-total span { font-family: Arial, sans-serif; font-size: 14px; font-weight: 600; }
          .summary-grand-total > strong { font-size: 27px; }
          .mobile-summary-toggle { display: flex; justify-content: flex-end; gap: 8px; width: 100%; margin: 8px 0 0; padding: 0; border: 0; background: none; color: var(--muted); font-size: 11px; }
          .place-order-card { padding: 0; border: 0; background: transparent; box-shadow: none; }
          .trust-points { display: none; }
          .place-order-button { min-height: 52px; font-size: 16px; }
          .place-order-card > p { padding: 10px 0 0; }
          .checkout-summary-column { padding: 14px; border: 1px solid var(--line); border-radius: 10px; background: rgba(255,250,244,.55); }
        }

        @media (max-width: 390px) {
          .checkout-shell { padding-inline: 10px; }
          .checkout-field-grid { gap: 13px 10px; }
          .checkout-field-grid label > span { font-size: 12px; }
          .pincode-row { grid-template-columns: 1fr 108px; }
          .summary-grand-total > strong { font-size: 24px; }
        }
      `}</style>
    </section>
  );
}

export default Checkout;
